import { useState } from 'react';
import {
  overwatchDefaults,
  overwatchFigureDefaults,
  overwatchMissionDefaults,
  overwatchPerchDefaults,
  overwatchPhaseDefaults,
  overwatchRulesDefaults,
  overwatchSceneDefaults,
  overwatchSpawnDefaults,
} from '../../../lib/s3/defaults';
import { parsePlanExport } from '../../../lib/s3/plan';
import {
  FIGURE_KINDS,
  NO_KILL_MODES,
  OW_OBJECTIVE_TYPES,
  OW_ROLES,
  ROUTINE_KINDS,
  TRAIT_AXES,
  type OverwatchDef,
  type OverwatchFigureDef,
  type OverwatchMissionDef,
  type OverwatchPerchDef,
  type OverwatchPhaseDef,
  type OverwatchRulesDef,
  type OverwatchSceneDef,
  type OverwatchSpawnDef,
  type OverwatchWavesDef,
} from '../../../lib/s3/types';
import { withDefaults } from '../../../lib/teamsDefaults';
import { CONDITION_IDS, type FieldErrors } from '../../../lib/validate';
import {
  CheckboxField,
  Field,
  Fieldset,
  IssueList,
  ListEditor,
  NumberField,
  NumberInput,
  SelectField,
  SelectInput,
  StringListEditor,
  TextField,
  inputCls,
  smallBtn,
} from '../fields';
import { useContentBundle } from '../refs';
import type { FormProps } from '../types';
import { OverwatchPlanPreview } from './OverwatchPlanPreview';
import { PlanView } from './PlanView';

const opts = (xs: readonly string[]) => xs.map((x) => ({ value: x, label: x }));
const TABS = ['Rules', 'Scenes', 'Figures', 'Traits', 'Missions', 'Plan preview'] as const;
type Tab = (typeof TABS)[number];

/** Issues at `prefix` and under it, flattened (for a block shown as one field). */
const under = (errors: FieldErrors, prefix: string) =>
  Object.entries(errors)
    .filter(([k]) => k === prefix || k.startsWith(`${prefix}.`))
    .flatMap(([, v]) => v ?? []);
/** The errors under `prefix.`, re-keyed without it (so a nested editor reads `id`, not `scenes.0.id`). */
const scoped = (errors: FieldErrors, prefix: string): FieldErrors =>
  Object.fromEntries(
    Object.entries(errors)
      .filter(([k]) => k.startsWith(`${prefix}.`))
      .map(([k, v]) => [k.slice(prefix.length + 1), v]),
  );
const count = (errors: FieldErrors, prefix: string) =>
  under(errors, prefix).filter((i) => i.severity === 'error').length;

/**
 * The overwatch section (sniper.html "Admin"): rules, scenes with their perches and the plan imported from Unity's OverwatchPlanExporter
 * (never hand-typed), figure archetypes, trait pools, missions with their phases, and the plan preview that plays a mission's timelines
 * from a seed through the API's OverwatchSim. Absent fields show the C# defaults.
 */
export function OverwatchForm({ value, onChange, errors }: FormProps<OverwatchDef>) {
  const ow = withDefaults(overwatchDefaults(), value ?? {}) as Required<OverwatchDef>;
  const [tab, setTab] = useState<Tab>('Rules');
  const set = <K extends keyof OverwatchDef>(k: K, x: OverwatchDef[K]) =>
    onChange({ ...ow, [k]: x });
  const tabKey: Record<Tab, string> = {
    Rules: 'rules',
    Scenes: 'scenes',
    Figures: 'figures',
    Traits: 'traits',
    Missions: 'missions',
    'Plan preview': '',
  };
  return (
    <>
      <div role="tablist" className="mb-3 flex flex-wrap gap-1">
        {TABS.map((t) => {
          const n = tabKey[t] ? count(errors, tabKey[t]) : 0;
          return (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={`${smallBtn} ${tab === t ? 'bg-ink text-newsprint' : ''}`}
            >
              {t}
              {n > 0 && <span className="ml-1 text-blam">● {n}</span>}
            </button>
          );
        })}
      </div>
      {tab === 'Rules' && (
        <RulesTab
          value={ow.rules ?? {}}
          missions={(ow.missions ?? []).map((m) => m?.id ?? '')}
          errors={scoped(errors, 'rules')}
          onChange={(r) => set('rules', r)}
        />
      )}
      {tab === 'Scenes' && (
        <ScenesTab
          scenes={ow.scenes ?? []}
          errors={errors}
          onChange={(x) => set('scenes', x as OverwatchSceneDef[])}
        />
      )}
      {tab === 'Figures' && (
        <FiguresTab
          figures={ow.figures ?? []}
          errors={errors}
          onChange={(x) => set('figures', x)}
        />
      )}
      {tab === 'Traits' && (
        <TraitsTab traits={ow.traits ?? {}} errors={errors} onChange={(x) => set('traits', x)} />
      )}
      {tab === 'Missions' && (
        <MissionsTab
          ow={ow}
          errors={errors}
          onChange={(x) => set('missions', x as OverwatchMissionDef[])}
        />
      )}
      {tab === 'Plan preview' && <OverwatchPlanPreview overwatch={ow} />}
    </>
  );
}

function RulesTab({
  value,
  onChange,
  errors,
  missions,
}: {
  value: OverwatchRulesDef;
  onChange: (r: OverwatchRulesDef) => void;
  errors: FieldErrors;
  missions: string[];
}) {
  const r = withDefaults(overwatchRulesDefaults(), value) as Required<OverwatchRulesDef>;
  const f = (
    label: string,
    k: keyof OverwatchRulesDef,
    o: { integer?: boolean; hint?: string } = {},
  ) => (
    <NumberField
      key={k}
      label={label}
      hint={o.hint}
      integer={o.integer}
      min={0}
      value={r[k] as number}
      issues={errors[k]}
      onChange={(n) => onChange({ ...r, [k]: n ?? 0 })}
    />
  );
  return (
    <>
      <Fieldset legend="Switch">
        <CheckboxField
          label="Enabled"
          hint="Off: no perches on the map and no overwatch tickets, without an app release."
          checked={!!r.enabled}
          onChange={(on) => onChange({ ...r, enabled: on })}
        />
        {f('Energy', 'energy', { hint: 'Flat team energy (shooter + optional spotter).' })}
      </Fieldset>
      <Fieldset legend="Rewards and costs">
        <Field
          label="Crate rolls C, B, A, S"
          hint={
            (r.crateRolls ?? []).length
              ? 'Clear all to use 1, 2, 3, 4.'
              : 'Empty: 1, 2, 3, 4 (shown).'
          }
          issues={[
            ...(errors.crateRolls ?? []),
            ...[0, 1, 2, 3].flatMap((i) => errors[`crateRolls.${i}`] ?? []),
          ]}
        >
          {() => (
            <div className="grid grid-cols-4 gap-1">
              {['C', 'B', 'A', 'S'].map((g, i) => {
                const rolls = (r.crateRolls ?? []).length === 4 ? r.crateRolls! : [1, 2, 3, 4];
                return (
                  <NumberInput
                    key={g}
                    label={`Crate rolls ${g}`}
                    integer
                    min={0}
                    max={10}
                    value={rolls[i]}
                    onChange={(x) => {
                      const next = [...rolls];
                      next[i] = x ?? 0;
                      onChange({ ...r, crateRolls: next });
                    }}
                  />
                );
              })}
            </div>
          )}
        </Field>
        <CheckboxField
          label="Default crate rolls (empty list)"
          checked={(r.crateRolls ?? []).length === 0}
          onChange={(on) => onChange({ ...r, crateRolls: on ? [] : [1, 2, 3, 4] })}
        />
        <SelectField
          label="One more shot condition"
          hint='The injury a member at the perch takes for "one more shot" (ADR 0031).'
          value={r.oneMoreShotCondition}
          options={opts(CONDITION_IDS)}
          issues={errors.oneMoreShotCondition}
          onChange={(x) => onChange({ ...r, oneMoreShotCondition: x ?? '' })}
        />
      </Fieldset>
      <Fieldset legend="Tower noise and relocation">
        {f('Fill per m of weapon noise', 'fillPerM')}
        {f('Decay %/s', 'decayPerS')}
        {f('Listen at (0..1)', 'listenAt', { hint: '"The dead are listening".' })}
        {f('Relocate noise to (0..1)', 'relocateNoiseTo')}
        {f('Relocate cost s', 'relocateCostS')}
        {f('Max relocations', 'maxRelocations', { integer: true })}
      </Fieldset>
      <Fieldset legend="Ballistics and hit test">
        {f('Gravity mult', 'gravityMult')}
        {f('Hit tolerance m', 'hitToleranceM')}
        {f('Sim Hz', 'simHz', { integer: true })}
        {f('Aim track Hz', 'aimTrackHz', { integer: true })}
        {f('Max aim rad/s', 'maxAimRadPerS')}
        {f('Min visible s', 'minVisibleS')}
        {f('Body radius m', 'bodyRadiusM')}
        {f('Body height m', 'bodyHeightM')}
        {f('Head radius m', 'headRadiusM')}
        {f('Head height m', 'headHeightM')}
        {f('Bystander flee m', 'bystanderFleeM')}
        {f('Bystander flee s', 'bystanderFleeS')}
      </Fieldset>
      <Fieldset legend="Perches on the map (M5, M34)">
        <StringListEditor
          label="Perch tags"
          base="perchTags"
          placeholder="man_made=water_tower"
          value={r.perchTags}
          errors={errors}
          onChange={(x) => onChange({ ...r, perchTags: x })}
        />
        {f('Min levels', 'minLevels', { integer: true })}
        {f('Min height m', 'minHeightM')}
        {f('Cooldown h', 'perchCooldownH')}
        {f('Most per set', 'maxPerSet', { integer: true, hint: 'Inside the cap of 4 specials.' })}
        {f('Import density max', 'importDensityMax', { integer: true })}
        {f('Import density radius m', 'importDensityRadiusM')}
        {f('Perch chance per set', 'perchChancePerSet', {
          hint: 'When a perch is in reach (0..1).',
        })}
        <SelectField
          label="Perch mission"
          hint="The mission a map perch runs (ow_perch_patrol)."
          value={r.perchMissionId}
          none="(none)"
          options={opts(missions.filter(Boolean))}
          issues={errors.perchMissionId}
          onChange={(x) => onChange({ ...r, perchMissionId: x ?? '' })}
        />
      </Fieldset>
    </>
  );
}

/** A `[a, b]` or `[a, b, c]` number tuple on one row. */
function Tuple({
  label,
  names,
  value,
  onChange,
  issues,
}: {
  label: string;
  names: string[];
  value: number[] | null | undefined;
  onChange: (v: number[]) => void;
  issues?: FieldErrors[string];
}) {
  const v = value ?? [];
  return (
    <Field label={label} issues={issues}>
      {() => (
        <div
          className="grid gap-1"
          style={{ gridTemplateColumns: `repeat(${names.length}, minmax(0, 1fr))` }}
        >
          {names.map((nm, i) => (
            <NumberInput
              key={nm}
              label={`${label} ${nm}`}
              value={v[i]}
              onChange={(x) => {
                const next = names.map((_, j) => v[j] ?? 0);
                next[i] = x ?? 0;
                onChange(next);
              }}
            />
          ))}
        </div>
      )}
    </Field>
  );
}

/** One entry of a list at a time: pick it with chips, add and remove. */
function Picker<T>({
  items,
  idOf,
  selected,
  setSelected,
  onChange,
  make,
  what,
  errors,
  base,
}: {
  items: (T | null)[];
  idOf: (x: T | null) => string;
  selected: number;
  setSelected: (i: number) => void;
  onChange: (x: (T | null)[]) => void;
  make: (n: number) => T;
  what: string;
  errors: FieldErrors;
  base: string;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-1">
      {items.map((x, i) => {
        const n = count(errors, `${base}.${i}`);
        return (
          <button
            key={i}
            type="button"
            className={`${smallBtn} font-mono ${i === selected ? 'bg-benday/40' : ''}`}
            onClick={() => setSelected(i)}
          >
            {idOf(x) || `(${what} ${i + 1})`}
            {n > 0 && <span className="ml-1 text-blam">● {n}</span>}
          </button>
        );
      })}
      <button
        type="button"
        className={smallBtn}
        onClick={() => {
          onChange([...items, make(items.length + 1)]);
          setSelected(items.length);
        }}
      >
        Add {what}
      </button>
      {items.length > 0 && selected < items.length && (
        <button
          type="button"
          className={`${smallBtn} text-blam`}
          onClick={() => {
            if (!confirm(`Remove ${what} "${idOf(items[selected] ?? null)}"?`)) return;
            onChange(items.filter((_, j) => j !== selected));
            setSelected(Math.max(0, selected - 1));
          }}
        >
          Remove {what}
        </button>
      )}
      <IssueList issues={errors[base]} />
    </div>
  );
}

function ScenesTab({
  scenes,
  onChange,
  errors,
}: {
  scenes: (OverwatchSceneDef | null)[];
  onChange: (x: (OverwatchSceneDef | null)[]) => void;
  errors: FieldErrors;
}) {
  const [sel, setSel] = useState(0);
  const [planText, setPlanText] = useState('');
  const [planMsg, setPlanMsg] = useState<string | null>(null);
  const raw = scenes[sel];
  const s = raw
    ? (withDefaults(overwatchSceneDefaults(), raw) as Required<OverwatchSceneDef>)
    : null;
  const base = `scenes.${sel}`;
  const put = (next: OverwatchSceneDef) => onChange(scenes.map((x, i) => (i === sel ? next : x)));
  const importPlan = (text: string) => {
    if (!s) return;
    try {
      const out = parsePlanExport(text);
      put({ ...s, plan: out.plan, ...(out.perches ? { perches: out.perches } : {}) });
      const p = out.plan;
      setPlanMsg(
        `Imported: ${Object.keys(p.zones ?? {}).length} zones, ${Object.keys(p.paths ?? {}).length} paths, ${(p.exits ?? []).length} exits, ${(p.occluders ?? []).length} occluders${out.perches ? `, ${out.perches.length} perches` : ''}.${out.sceneId && out.sceneId !== s.id ? ` (The file is for '${out.sceneId}'.)` : ''}`,
      );
      setPlanText('');
    } catch (e) {
      setPlanMsg(e instanceof Error ? e.message : String(e));
    }
  };
  const plan = s?.plan ?? {};
  return (
    <>
      <Picker
        items={scenes}
        idOf={(x) => x?.id ?? ''}
        selected={sel}
        setSelected={setSel}
        onChange={onChange}
        make={(n) => ({ ...overwatchSceneDefaults(`scene_${n}`), name: 'New scene' })}
        what="scene"
        errors={errors}
        base="scenes"
      />
      {s && (
        <>
          <Fieldset legend="Scene">
            <TextField
              label="Id"
              mono
              value={s.id}
              issues={errors[`${base}.id`]}
              onChange={(x) => put({ ...s, id: x })}
            />
            <TextField
              label="Name"
              value={s.name}
              issues={errors[`${base}.name`]}
              onChange={(x) => put({ ...s, name: x })}
            />
            <TextField
              label="Prefab"
              mono
              hint="Resources path (Overwatch/MainStreet)."
              value={s.prefab}
              issues={errors[`${base}.prefab`]}
              onChange={(x) => put({ ...s, prefab: x })}
            />
          </Fieldset>
          <Fieldset legend="Perches">
            <ListEditor<OverwatchPerchDef>
              label="Perches (the first is the default)"
              what="perch"
              items={(s.perches ?? []) as OverwatchPerchDef[]}
              issues={errors[`${base}.perches`]}
              make={() => overwatchPerchDefaults(`perch_${(s.perches ?? []).length + 1}`)}
              onChange={(x) => put({ ...s, perches: x })}
              row={(p, j, setP) => {
                const pb = `${base}.perches.${j}`;
                return (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <TextField
                      label="Perch id"
                      mono
                      value={p.id}
                      issues={errors[`${pb}.id`]}
                      onChange={(x) => setP({ ...p, id: x })}
                    />
                    <TextField
                      label="Perch name"
                      value={p.name}
                      issues={errors[`${pb}.name`]}
                      onChange={(x) => setP({ ...p, name: x })}
                    />
                    <Tuple
                      label="Eye (x, height, z)"
                      names={['x', 'y', 'z']}
                      value={p.pos}
                      issues={under(errors, `${pb}.pos`)}
                      onChange={(v) => setP({ ...p, pos: v })}
                    />
                    <Tuple
                      label="Yaw arc °"
                      names={['min', 'max']}
                      value={p.yawDeg}
                      issues={under(errors, `${pb}.yawDeg`)}
                      onChange={(v) => setP({ ...p, yawDeg: v })}
                    />
                    <Tuple
                      label="Pitch arc °"
                      names={['min', 'max']}
                      value={p.pitchDeg}
                      issues={under(errors, `${pb}.pitchDeg`)}
                      onChange={(v) => setP({ ...p, pitchDeg: v })}
                    />
                    <TextField
                      label="Foreground"
                      mono
                      value={p.foreground}
                      issues={errors[`${pb}.foreground`]}
                      onChange={(x) => setP({ ...p, foreground: x })}
                    />
                  </div>
                );
              }}
            />
          </Fieldset>
          <fieldset className="mb-4 rounded border-2 border-ink bg-white p-3">
            <legend className="px-1 text-sm font-bold">Plan (imported)</legend>
            <p className="mb-2 text-xs text-bruise">
              {Object.keys(plan.zones ?? {}).length} zones · {Object.keys(plan.paths ?? {}).length}{' '}
              paths · {(plan.exits ?? []).length} exits ({(plan.exits ?? []).join(', ') || 'none'})
              · {(plan.occluders ?? []).length} occluders. The plan comes from Unity&apos;s
              OverwatchPlanExporter; paste or load its JSON to replace it.
            </p>
            <IssueList issues={under(errors, `${base}.plan`)} />
            <PlanView
              plan={plan}
              perches={(s.perches ?? []) as OverwatchPerchDef[]}
              label={`Scene ${s.id} plan`}
            />
            <textarea
              aria-label="Plan export JSON"
              className={`${inputCls} mt-2 h-24 font-mono text-xs`}
              placeholder='{"sceneId": "main_street", "plan": {"zones": {...}, "paths": {...}, "exits": [...], "occluders": [...]}}'
              value={planText}
              onChange={(e) => setPlanText(e.target.value)}
            />
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={smallBtn}
                disabled={!planText.trim()}
                onClick={() => importPlan(planText)}
              >
                Import the pasted plan
              </button>
              <label className="text-xs">
                or load a file{' '}
                <input
                  aria-label="Plan export file"
                  type="file"
                  accept="application/json,.json"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void file.text().then(importPlan);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            {planMsg && (
              <p className="mt-1 text-xs" role="status">
                {planMsg}
              </p>
            )}
          </fieldset>
        </>
      )}
    </>
  );
}

function FiguresTab({
  figures,
  onChange,
  errors,
}: {
  figures: (OverwatchFigureDef | null)[];
  onChange: (x: OverwatchFigureDef[]) => void;
  errors: FieldErrors;
}) {
  const zombies = (useContentBundle().zombies ?? []).map((z) => z?.id ?? '').filter(Boolean);
  return (
    <Fieldset legend="Figure archetypes">
      <ListEditor<OverwatchFigureDef>
        label="Figures"
        what="figure"
        hint="Humans have hp and a role; zombie figures take their stats from zombies[]."
        items={figures as OverwatchFigureDef[]}
        issues={errors.figures}
        make={() => overwatchFigureDefaults(`figure_${figures.length + 1}`)}
        onChange={onChange}
        row={(raw, j, setF) => {
          const fg = withDefaults(overwatchFigureDefaults(), raw) as Required<OverwatchFigureDef>;
          const fb = `figures.${j}`;
          const n = (label: string, k: 'hp' | 'walkMps' | 'runMps', integer?: boolean) => (
            <NumberField
              label={label}
              integer={integer}
              min={0}
              value={fg[k]}
              issues={errors[`${fb}.${k}`]}
              onChange={(x) => setF({ ...fg, [k]: x ?? 0 })}
            />
          );
          return (
            <div className="grid gap-2 sm:grid-cols-3">
              <TextField
                label="Figure id"
                mono
                value={fg.id}
                issues={errors[`${fb}.id`]}
                onChange={(x) => setF({ ...fg, id: x })}
              />
              <TextField
                label="Figure name"
                value={fg.name}
                issues={errors[`${fb}.name`]}
                onChange={(x) => setF({ ...fg, name: x })}
              />
              <SelectField
                label="Kind"
                value={fg.kind}
                options={opts(FIGURE_KINDS)}
                issues={errors[`${fb}.kind`]}
                onChange={(x) => setF({ ...fg, kind: x ?? '' })}
              />
              {fg.kind === 'zombie' && (
                <SelectField
                  label="Zombie"
                  value={fg.zombieId}
                  options={opts(zombies)}
                  none="(none)"
                  issues={errors[`${fb}.zombieId`]}
                  onChange={(x) => setF({ ...fg, zombieId: x ?? '' })}
                />
              )}
              <SelectField
                label="Role"
                value={fg.role}
                options={opts(OW_ROLES)}
                issues={errors[`${fb}.role`]}
                onChange={(x) => setF({ ...fg, role: x ?? '' })}
              />
              {n('HP', 'hp', true)}
              {n('Walk m/s', 'walkMps')}
              {n('Run m/s', 'runMps')}
              <div className="sm:col-span-3 grid gap-2 sm:grid-cols-4">
                <CheckboxField
                  label="Reveal"
                  checked={!!fg.reveal}
                  onChange={(on) =>
                    setF({ ...fg, reveal: on ? { kind: 'glint', everyS: 6 } : null })
                  }
                />
                {fg.reveal && (
                  <>
                    <TextField
                      label="Reveal kind"
                      mono
                      value={fg.reveal.kind}
                      issues={errors[`${fb}.reveal.kind`]}
                      onChange={(x) => setF({ ...fg, reveal: { ...fg.reveal, kind: x } })}
                    />
                    <NumberField
                      label="Reveal every s"
                      min={0}
                      value={fg.reveal.everyS}
                      issues={errors[`${fb}.reveal.everyS`]}
                      onChange={(x) => setF({ ...fg, reveal: { ...fg.reveal, everyS: x ?? 0 } })}
                    />
                  </>
                )}
              </div>
              <div className="sm:col-span-3 grid gap-2 sm:grid-cols-4">
                <CheckboxField
                  label="Threat timer"
                  checked={!!fg.threat}
                  onChange={(on) => setF({ ...fg, threat: on ? { firstS: 15, everyS: 15 } : null })}
                />
                {fg.threat && (
                  <>
                    <NumberField
                      label="Threat first s"
                      min={0}
                      value={fg.threat.firstS}
                      issues={errors[`${fb}.threat.firstS`]}
                      onChange={(x) => setF({ ...fg, threat: { ...fg.threat, firstS: x ?? 0 } })}
                    />
                    <NumberField
                      label="Threat every s"
                      min={0}
                      value={fg.threat.everyS}
                      issues={errors[`${fb}.threat.everyS`]}
                      onChange={(x) => setF({ ...fg, threat: { ...fg.threat, everyS: x ?? 0 } })}
                    />
                  </>
                )}
              </div>
            </div>
          );
        }}
      />
    </Fieldset>
  );
}

function TraitsTab({
  traits,
  onChange,
  errors,
}: {
  traits: Record<string, string[]>;
  onChange: (x: Record<string, string[]>) => void;
  errors: FieldErrors;
}) {
  const axes = [...new Set([...TRAIT_AXES, ...Object.keys(traits)])];
  return (
    <>
      <p className="mb-2 text-xs text-bruise">
        Trait pools by axis: the roster draws from these, the description names them, decoys share
        exactly two. M36: brown renders as dark ink, so the jacket value is &ldquo;dark&rdquo;.
      </p>
      <IssueList issues={errors.traits} />
      <div className="grid gap-3 sm:grid-cols-2">
        {axes.map((axis) => (
          <div key={axis} className="rounded border-2 border-ink bg-white p-2">
            <StringListEditor
              label={axis}
              base={`traits.${axis}`}
              value={traits[axis]}
              errors={errors}
              onChange={(x) => onChange({ ...traits, [axis]: x })}
            />
            {!(axis in traits) && <p className="text-xs text-bruise">Not in the draft yet.</p>}
          </div>
        ))}
      </div>
    </>
  );
}

function MissionsTab({
  ow,
  onChange,
  errors,
}: {
  ow: Required<OverwatchDef>;
  onChange: (x: (OverwatchMissionDef | null)[]) => void;
  errors: FieldErrors;
}) {
  const b = useContentBundle();
  const [sel, setSel] = useState(0);
  const missions = ow.missions ?? [];
  const raw = missions[sel];
  const m = raw
    ? (withDefaults(overwatchMissionDefaults(), raw) as Required<OverwatchMissionDef>)
    : null;
  const base = `missions.${sel}`;
  const put = (next: OverwatchMissionDef) =>
    onChange(missions.map((x, i) => (i === sel ? next : x)));
  const scene = (ow.scenes ?? []).find((s) => s?.id === m?.sceneId);
  const perches = opts((scene?.perches ?? []).map((p) => p?.id ?? '').filter(Boolean));
  const beats = opts((b.beatChains ?? []).map((c) => c?.id ?? '').filter(Boolean));
  const tables = opts((b.lootTables ?? []).map((t) => t?.id ?? '').filter(Boolean));
  const grades = m?.grades ?? [];
  return (
    <>
      <Picker
        items={missions}
        idOf={(x) => x?.id ?? ''}
        selected={sel}
        setSelected={setSel}
        onChange={onChange}
        make={(n) => ({ ...overwatchMissionDefaults(`ow_mission_${n}`), name: 'New mission' })}
        what="mission"
        errors={errors}
        base="missions"
      />
      {m && (
        <>
          <Fieldset legend="Mission">
            <TextField
              label="Id"
              mono
              value={m.id}
              issues={errors[`${base}.id`]}
              onChange={(x) => put({ ...m, id: x })}
            />
            <TextField
              label="Name"
              value={m.name}
              issues={errors[`${base}.name`]}
              onChange={(x) => put({ ...m, name: x })}
            />
            <SelectField
              label="Scene"
              value={m.sceneId}
              options={opts((ow.scenes ?? []).map((s) => s?.id ?? '').filter(Boolean))}
              issues={errors[`${base}.sceneId`]}
              onChange={(x) => put({ ...m, sceneId: x ?? '' })}
            />
            <SelectField
              label="Perch"
              value={m.perchId}
              options={perches}
              issues={errors[`${base}.perchId`]}
              onChange={(x) => put({ ...m, perchId: x ?? '' })}
            />
            <SelectField
              label="Relocation perch"
              value={m.altPerchId}
              none="(none)"
              options={perches}
              issues={errors[`${base}.altPerchId`]}
              onChange={(x) => put({ ...m, altPerchId: x ?? '' })}
            />
            <NumberField
              label="Shot cap"
              integer
              min={0}
              value={m.shotCap}
              issues={errors[`${base}.shotCap`]}
              onChange={(x) => put({ ...m, shotCap: x ?? 0 })}
            />
            <StringListEditor
              label="Required rifles"
              hint="Scoped weapon ids that can play this mission (empty = any scoped rifle); without one the perch answers 409 needs_weapon."
              base={`${base}.requiredWeaponIds`}
              value={m.requiredWeaponIds ?? []}
              errors={errors}
              onChange={(x) => put({ ...m, requiredWeaponIds: x })}
            />
            <SelectField
              label="Briefing beat"
              value={m.briefingBeat}
              none="(none)"
              options={beats}
              issues={errors[`${base}.briefingBeat`]}
              onChange={(x) => put({ ...m, briefingBeat: x ?? '' })}
            />
            <SelectField
              label="Don't shoot"
              hint="soft: −250 and a ledger entry; hard: the phase fails."
              value={m.noKill}
              options={opts(NO_KILL_MODES)}
              issues={errors[`${base}.noKill`]}
              onChange={(x) => put({ ...m, noKill: x ?? '' })}
            />
            <SelectField
              label="Reward loot table"
              value={m.rewardLootTable}
              none="(none)"
              options={tables}
              issues={errors[`${base}.rewardLootTable`]}
              onChange={(x) => put({ ...m, rewardLootTable: x ?? '' })}
            />
            <Field label="Grades C, B, A, S (ascending)" issues={under(errors, `${base}.grades`)}>
              {() => (
                <div className="grid grid-cols-4 gap-1">
                  {['C', 'B', 'A', 'S'].map((g, i) => (
                    <NumberInput
                      key={g}
                      label={`Grade ${g}`}
                      integer
                      min={0}
                      value={grades[i]}
                      onChange={(x) => {
                        const next = [0, 1, 2, 3].map((j) => grades[j] ?? 0);
                        next[i] = x ?? 0;
                        put({ ...m, grades: next });
                      }}
                    />
                  ))}
                </div>
              )}
            </Field>
          </Fieldset>
          <Fieldset legend="Phases">
            <ListEditor<OverwatchPhaseDef>
              label="Phases (in order)"
              what="phase"
              items={(m.phases ?? []) as OverwatchPhaseDef[]}
              issues={errors[`${base}.phases`]}
              make={() => overwatchPhaseDefaults(`phase_${(m.phases ?? []).length + 1}`)}
              onChange={(x) => put({ ...m, phases: x })}
              row={(p, j, setP) => (
                <PhaseEditor
                  phase={p}
                  onChange={setP}
                  errors={errors}
                  base={`${base}.phases.${j}`}
                  ow={ow}
                  sceneZones={Object.keys(scene?.plan?.zones ?? {})}
                  scenePaths={Object.keys(scene?.plan?.paths ?? {})}
                  exits={scene?.plan?.exits ?? []}
                />
              )}
            />
          </Fieldset>
        </>
      )}
    </>
  );
}

function PhaseEditor({
  phase,
  onChange,
  errors,
  base,
  ow,
  sceneZones,
  scenePaths,
  exits,
}: {
  phase: OverwatchPhaseDef;
  onChange: (p: OverwatchPhaseDef) => void;
  errors: FieldErrors;
  base: string;
  ow: Required<OverwatchDef>;
  sceneZones: string[];
  scenePaths: string[];
  exits: string[];
}) {
  const p = withDefaults(overwatchPhaseDefaults(), phase) as Required<OverwatchPhaseDef>;
  const o = { ...overwatchPhaseDefaults().objective, ...(p.objective ?? {}) };
  const figures = opts((ow.figures ?? []).map((f) => f?.id ?? '').filter(Boolean));
  const axes = [...new Set([...TRAIT_AXES, ...Object.keys(ow.traits ?? {})])];
  const describe = new Set(o.describe ?? []);
  const [hiddenText, setHiddenText] = useState(() => JSON.stringify(p.hidden ?? {}));
  const [hiddenErr, setHiddenErr] = useState<string | null>(null);
  const setO = (x: Partial<typeof o>) => onChange({ ...p, objective: { ...o, ...x } });
  const waves = p.waves;
  const setW = (x: Partial<OverwatchWavesDef>) => onChange({ ...p, waves: { ...waves, ...x } });
  return (
    <div className="grid gap-2 sm:grid-cols-3">
      <TextField
        label="Phase id"
        mono
        value={p.id}
        issues={errors[`${base}.id`]}
        onChange={(x) => onChange({ ...p, id: x })}
      />
      <NumberField
        label="Clock s"
        min={0}
        value={p.timeS}
        issues={errors[`${base}.timeS`]}
        onChange={(x) => onChange({ ...p, timeS: x ?? 0 })}
      />
      <TextField
        label="Reveal beat"
        mono
        value={p.revealBeat}
        issues={errors[`${base}.revealBeat`]}
        onChange={(x) => onChange({ ...p, revealBeat: x })}
      />
      <div className="sm:col-span-3 rounded border border-newsprint p-2">
        <span className="text-xs font-semibold uppercase tracking-wide">Objective</span>
        <div className="grid gap-2 sm:grid-cols-3">
          <SelectField
            label="Type"
            value={o.type}
            options={opts(OW_OBJECTIVE_TYPES)}
            issues={errors[`${base}.objective.type`]}
            onChange={(x) => setO({ type: x ?? '' })}
          />
          {o.type === 'kill_target' && (
            <>
              <NumberField
                label="Decoys"
                integer
                min={0}
                value={o.decoys}
                issues={errors[`${base}.objective.decoys`]}
                onChange={(x) => setO({ decoys: x ?? 0 })}
              />
              <NumberField
                label="Flee on a miss within m"
                min={0}
                value={o.fleeOnMissWithinM}
                issues={errors[`${base}.objective.fleeOnMissWithinM`]}
                onChange={(x) => setO({ fleeOnMissWithinM: x ?? 0 })}
              />
              <TextField
                label="Hidden var"
                mono
                hint="figure.bitten, or empty."
                value={o.hidden}
                issues={errors[`${base}.objective.hidden`]}
                onChange={(x) => setO({ hidden: x })}
              />
              <SelectField
                label="Named figure"
                none="(none)"
                value={o.figure}
                options={figures}
                issues={errors[`${base}.objective.figure`]}
                onChange={(x) => setO({ figure: x ?? '' })}
              />
              <div className="sm:col-span-3">
                <span className="block text-xs font-semibold uppercase tracking-wide">
                  Describe by
                </span>
                <div className="flex flex-wrap gap-3">
                  {axes.map((a) => (
                    <CheckboxField
                      key={a}
                      label={a}
                      checked={describe.has(a)}
                      onChange={(on) =>
                        setO({
                          describe: on
                            ? [...(o.describe ?? []), a]
                            : (o.describe ?? []).filter((x) => x !== a),
                        })
                      }
                    />
                  ))}
                </div>
                <IssueList issues={under(errors, `${base}.objective.describe`)} />
              </div>
            </>
          )}
          {o.type === 'escort' && (
            <SelectField
              label="Escort path"
              value={o.path}
              options={opts(scenePaths)}
              issues={errors[`${base}.objective.path`]}
              onChange={(x) => setO({ path: x ?? '' })}
            />
          )}
        </div>
        <IssueList issues={errors[`${base}.objective`]} />
      </div>
      <div className="sm:col-span-3">
        <ListEditor<OverwatchSpawnDef>
          label="Spawns"
          what="spawn"
          items={(p.spawns ?? []) as OverwatchSpawnDef[]}
          issues={errors[`${base}.spawns`]}
          make={overwatchSpawnDefaults}
          onChange={(x) => onChange({ ...p, spawns: x })}
          row={(s, k, setS) => {
            const sb = `${base}.spawns.${k}`;
            const pick = (
              label: string,
              key: keyof OverwatchSpawnDef,
              options: string[],
              none?: string,
            ) => (
              <div>
                <SelectInput
                  label={`Spawn ${k + 1} ${label}`}
                  value={s[key] as string}
                  none={none}
                  options={opts(options)}
                  onChange={(x) => setS({ ...s, [key]: x ?? '' })}
                />
                <IssueList issues={errors[`${sb}.${key}`]} />
              </div>
            );
            return (
              <div className="grid grid-cols-5 gap-1">
                {pick(
                  'figure',
                  'figure',
                  figures.map((x) => x.value),
                )}
                <div>
                  <NumberInput
                    label={`Spawn ${k + 1} count`}
                    integer
                    min={0}
                    value={s.count}
                    onChange={(x) => setS({ ...s, count: x ?? 0 })}
                  />
                  <IssueList issues={errors[`${sb}.count`]} />
                </div>
                {pick('routine', 'routine', [...ROUTINE_KINDS])}
                {pick('zone', 'zone', sceneZones, '(none)')}
                {pick('path', 'path', scenePaths, '(none)')}
              </div>
            );
          }}
        />
      </div>
      <Field
        label="Hidden timings"
        hint='Rolled at issue: {"turnAtS": [26, 38]}.'
        issues={[...under(errors, `${base}.hidden`)]}
        className="sm:col-span-2"
      >
        {(id) => (
          <>
            <input
              id={id}
              className={`${inputCls} font-mono`}
              value={hiddenText}
              onChange={(e) => {
                setHiddenText(e.target.value);
                try {
                  const v = JSON.parse(e.target.value) as unknown;
                  if (!v || typeof v !== 'object' || Array.isArray(v))
                    throw new Error('An object of name → [min, max].');
                  setHiddenErr(null);
                  onChange({ ...p, hidden: v as Record<string, number[]> });
                } catch (err) {
                  setHiddenErr(err instanceof Error ? err.message : String(err));
                }
              }}
            />
            {hiddenErr && <p className="text-xs text-blam">{hiddenErr}</p>}
          </>
        )}
      </Field>
      <StringListEditor
        label="Decoy tells"
        base={`${base}.decoyTells`}
        placeholder="bandage_arm"
        value={p.decoyTells}
        errors={errors}
        onChange={(x) => onChange({ ...p, decoyTells: x })}
      />
      <div className="sm:col-span-3 rounded border border-newsprint p-2">
        <CheckboxField
          label="Zombie waves"
          checked={!!waves}
          onChange={(on) =>
            onChange({ ...p, waves: on ? { everyS: 6, count: 1, runnersAtS: [], from: [] } : null })
          }
        />
        {waves && (
          <div className="grid gap-2 sm:grid-cols-2">
            <NumberField
              label="Every s"
              min={0}
              value={waves.everyS}
              issues={errors[`${base}.waves.everyS`]}
              onChange={(x) => setW({ everyS: x ?? 0 })}
            />
            <NumberField
              label="Count"
              integer
              min={0}
              value={waves.count}
              issues={errors[`${base}.waves.count`]}
              onChange={(x) => setW({ count: x ?? 0 })}
            />
            <Field
              label="Runners at s"
              hint="Comma-separated."
              issues={under(errors, `${base}.waves.runnersAtS`)}
            >
              {(id) => (
                <input
                  id={id}
                  className={inputCls}
                  defaultValue={(waves.runnersAtS ?? []).join(', ')}
                  onBlur={(e) =>
                    setW({
                      runnersAtS: e.target.value
                        .split(',')
                        .map((x) => Number(x.trim()))
                        .filter((x) => x.toString() !== 'NaN' && e.target.value.trim() !== ''),
                    })
                  }
                />
              )}
            </Field>
            <div>
              <span className="block text-xs font-semibold uppercase tracking-wide">From</span>
              <div className="flex flex-wrap gap-3">
                {[...new Set([...exits, ...sceneZones])].map((x) => (
                  <CheckboxField
                    key={x}
                    label={x}
                    checked={(waves.from ?? []).includes(x)}
                    onChange={(on) =>
                      setW({
                        from: on
                          ? [...(waves.from ?? []), x]
                          : (waves.from ?? []).filter((y) => y !== x),
                      })
                    }
                  />
                ))}
              </div>
              <IssueList issues={under(errors, `${base}.waves.from`)} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
